const RISK_ORDER = {
  SAFE: 0,
  LOW: 0,
  MEDIUM: 1,
  STANDARD: 1,
  HIGH: 2,
  HIGH_RISK: 2,
  CRITICAL: 3,
  BLOCKED: 4,
};

const strongerRisk = (current = "MEDIUM", discovered = "MEDIUM") => (
  (RISK_ORDER[discovered] ?? 1) > (RISK_ORDER[current] ?? 1) ? discovered : current
);

export const createDefaultPolicyDraft = (values) => JSON.stringify({
  qz_pol: {
    mcp_connection: values.mcp_connection,
    mcp_auth_token: values.mcp_auth_token || "bearer_token_placeholder",
    auth_format: values.auth_format || "raw",
    ...(values.auth_header ? { auth_header: values.auth_header } : {}),
    allowed_intents: {
      read: {
        risk_level: "SAFE",
        requires_approval: false,
        description: "Read operations",
      },
    },
    fallback_policy: {
      unknown_intents: {
        default_action: "require_approval",
        rules: [
          {
            condition: { confidence: { min: 0.8 }, severity_score: { min: 0.5 } },
            action: "require_approval",
            risk_level: "HIGH_RISK",
            confirmation_text: "Unknown operation requires approval",
          },
          {
            condition: { confidence: { max: 0.6 } },
            action: "block",
            reason: "Intent unclear - low confidence",
          },
        ],
      },
    },
    rate_limits: { per_user: 100, per_org: 1000 },
    allowed_roles: ["all"],
    provider: values.provider,
    version: "1.0.0",
  },
  initial_data: { safe_status: "SAFE", risk_score: 0, last_verified_at: null },
}, null, 2);

export const createDefaultToolsDraft = () => JSON.stringify({ tools: [] }, null, 2);

export const mergeDiscoveredToolDrafts = (policyJson, toolsJson, selectedTools) => {
  const policy = JSON.parse(policyJson);
  const tools = JSON.parse(toolsJson);
  const selectedNames = selectedTools.map((tool) => tool.name);
  const existingAllowedIntents = policy.qz_pol?.allowed_intents || {};
  const allowedIntents = {};

  selectedTools.forEach((tool) => {
    const governance = tool.governance || {};
    const action = governance.action || "unknown";
    const current = existingAllowedIntents[action] || {};
    const riskLevel = strongerRisk(current.risk_level, governance.risk_level);
    allowedIntents[action] = {
      ...current,
      risk_level: riskLevel,
      requires_approval: Boolean(
        current.requires_approval
        || governance.consequential
        || riskLevel !== "SAFE"
      ),
      ...(current.description
        ? {}
        : { description: `${action[0].toUpperCase()}${action.slice(1)} operations` }),
    };
  });

  return {
    policy: JSON.stringify({
      ...policy,
      qz_pol: { ...(policy.qz_pol || {}), allowed_intents: allowedIntents },
    }, null, 2),
    tools: JSON.stringify({
      ...tools,
      tools: [...new Set(selectedNames)],
    }, null, 2),
  };
};

const toolNamesIn = (json) => {
  const parsed = JSON.parse(json);
  return Array.isArray(parsed.tools) ? parsed.tools : [];
};

const intentNamesIn = (json) => Object.keys(JSON.parse(json).qz_pol?.allowed_intents || {});

// Set-based, not count-based: regenerating with a changed selection can swap one
// name for another, which leaves the totals identical.
const countDelta = (before, after) => ({
  added: after.filter((name) => !before.includes(name)).length,
  removed: before.filter((name) => !after.includes(name)).length,
});

const pluralize = (count, noun) => `${count} ${noun}${count === 1 ? "" : "s"}`;

/**
 * Human-readable summary of what a generation actually changed, so the action
 * reports a concrete outcome instead of silently mutating drafts.
 */
export const summarizeDraftChanges = (before, after, promptNote) => {
  const changed = ["policy", "tools", "prompt"].filter((key) => before[key] !== after[key]);
  if (changed.length === 0) {
    return "No changes — the selected tools are already in your drafts.";
  }

  const tools = countDelta(toolNamesIn(before.tools), toolNamesIn(after.tools));
  const intents = countDelta(intentNamesIn(before.policy), intentNamesIn(after.policy));

  const parts = [];
  if (tools.added) parts.push(`${pluralize(tools.added, "tool")} added`);
  if (tools.removed) parts.push(`${pluralize(tools.removed, "tool")} removed`);
  if (intents.added) parts.push(`${pluralize(intents.added, "policy intent")} added`);
  if (intents.removed) parts.push(`${pluralize(intents.removed, "policy intent")} removed`);
  if (parts.length === 0 && changed.includes("policy")) parts.push("policy updated");
  parts.push(promptNote);

  return `Drafts updated — ${parts.join(", ")}.`;
};